import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Play, Loader2, AlertCircle, Trash2, ChevronDown, ChevronUp, Info } from "lucide-react";

/**
 * OCC Command Panel
 * -----------------
 * Embedded in the CloudDetailPage. Lets the operator fire ad-hoc OCC
 * commands at the cloud's worker VM and inspect stdout/stderr/exit code
 * inline. Each invocation lands as a single audit_event on the backend.
 *
 * Architecture:
 *   - We POST /api/admin/clouds/{id}/occ → backend resolves the worker
 *     VM by cloud_id, runs `sudo -u www-data php occ <argv>` via
 *     Proxmox `qm guest exec`, captures the output, returns it here.
 *   - The CM allow-list (visible via `GET .../occ/allowlist`) only
 *     applies to the reseller-facing v1 endpoint. Superadmin in this
 *     panel can run ANY OCC verb.
 *   - "Warn verbs" (user:delete, app:remove, db:convert-type,
 *     maintenance:install, maintenance:data-fingerprint-reset) get a
 *     soft confirm-step before submit. The backend still allows them
 *     but tags the audit row with `cloud.occ.exec.warn`.
 *
 * History stays in component state — no localStorage, since OCC output
 * can contain sensitive data (DB names, usernames). On unmount /
 * cloud-switch the history is wiped.
 */

const WARN_VERBS = new Set([
    "user:delete",
    "app:remove",
    "db:convert-type",
    "maintenance:install",
    "maintenance:data-fingerprint-reset",
]);

const QUICK_COMMANDS = [
    { label: "Status", args: ["status"] },
    { label: "Config list", args: ["config:list", "system"] },
    { label: "List apps", args: ["app:list"] },
    { label: "List users", args: ["user:list"] },
    { label: "Setup checks", args: ["setupchecks"] },
    { label: "Maintenance repair", args: ["maintenance:repair", "--include-expensive"] },
];


// Long-running verbs (AppAPI ExApp register/deploy pull multi-GB Docker
// images, occ upgrade, big repairs, …) must not be cut early. Mirror the
// backend's OCC_LONG_VERB_PREFIXES; the server also enforces a 30-min floor.
const LONG_VERB_PREFIXES = ["app_api", "app:install", "app:update", "upgrade",
    "maintenance:repair", "db:add-missing", "db:convert", "files:scan",
    "preview:pre-generate"];

function timeoutFor(argv) {
    const verb = (argv && argv[0]) || "";
    return LONG_VERB_PREFIXES.some((p) => verb.startsWith(p)) ? 1800 : 300;
}


function parseArgv(line) {
    // Parse a shell-ish input line into argv[] respecting quotes.
    // Falls back to whitespace-split for the common case.
    const out = [];
    let cur = "";
    let q = null;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (q) {
            if (ch === q) {
                q = null;
                continue;
            }
            cur += ch;
        } else if (ch === '"' || ch === "'") {
            q = ch;
        } else if (/\s/.test(ch)) {
            if (cur) {
                out.push(cur);
                cur = "";
            }
        } else {
            cur += ch;
        }
    }
    if (cur) out.push(cur);
    return out;
}


export default function CloudOccPanel({ cloudId, ncInstallState }) {
    const [cmd, setCmd] = useState("");
    const [running, setRunning] = useState(false);
    const [history, setHistory] = useState([]); // newest first
    const [expanded, setExpanded] = useState(true);
    const inputRef = useRef(null);

    const ready = ncInstallState === "installed";

    const send = async (argvOverride = null) => {
        const argv = argvOverride || parseArgv(cmd.trim());
        if (!argv.length) return;
        if (WARN_VERBS.has(argv[0])) {
            const ok = window.confirm(
                `'${argv[0]}' can delete data or make the cloud unusable.\n\n` +
                `Argv: ${JSON.stringify(argv)}\n\nReally run it?`,
            );
            if (!ok) return;
        }
        setRunning(true);
        const startedAt = new Date();
        try {
            const r = await api.post(`/api/admin/clouds/${cloudId}/occ`, {
                args: argv,
                timeout_s: timeoutFor(argv),
            });
            setHistory((prev) => [
                { ...r.data, startedAt: startedAt.toISOString() },
                ...prev,
            ].slice(0, 25));
            if (!argvOverride) setCmd("");
        } catch (e) {
            setHistory((prev) => [
                {
                    error: formatApiError(e),
                    argv,
                    startedAt: startedAt.toISOString(),
                    exit_code: -1,
                    stdout: "",
                    stderr: "",
                    duration_ms: 0,
                },
                ...prev,
            ].slice(0, 25));
        } finally {
            setRunning(false);
            inputRef.current?.focus();
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    return (
        <div
            data-testid="cloud-occ-panel"
            className="cm-card p-5"
        >
            <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpanded((x) => !x)}
                data-testid="occ-panel-toggle"
            >
                <div className="cm-h3 flex items-center gap-2">
                    <span>OCC command console</span>
                    {!ready && (
                        <span className="text-[11px] font-mono text-amber-400 bg-amber-950/40 border border-amber-900 px-2 py-0.5 rounded">
                            waiting for install_state=installed
                        </span>
                    )}
                </div>
                {expanded ? (
                    <ChevronUp className="w-4 h-4 text-zinc-500" />
                ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-500" />
                )}
            </div>

            {expanded && (
                <>
                    {/* Quick commands */}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {QUICK_COMMANDS.map((qc) => (
                            <button
                                key={qc.label}
                                type="button"
                                disabled={!ready || running}
                                onClick={() => send(qc.args)}
                                className="cm-btn-ghost text-xs py-1 px-2 font-mono disabled:opacity-30"
                                data-testid={`occ-quick-${qc.args[0].replace(/[:_]/g, "-")}`}
                            >
                                {qc.label}
                            </button>
                        ))}
                    </div>

                    {/* Input row */}
                    <div className="mt-3 flex gap-2 items-stretch">
                        <div className="flex-1 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-xs select-none">
                                occ
                            </span>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="status     |     config:system:get instanceid     |     app:list"
                                value={cmd}
                                onChange={(e) => setCmd(e.target.value)}
                                onKeyDown={onKeyDown}
                                disabled={!ready || running}
                                className="w-full bg-zinc-950 border border-zinc-800 focus:border-zinc-600 outline-none rounded-md py-2 pl-12 pr-3 text-sm font-mono text-zinc-200 disabled:opacity-40 placeholder:text-zinc-700"
                                data-testid="occ-command-input"
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => send()}
                            disabled={!ready || running || !cmd.trim()}
                            className="cm-btn-primary flex items-center gap-1.5 px-4 text-xs disabled:opacity-40"
                            data-testid="occ-submit-button"
                        >
                            {running ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                                <Play className="w-3.5 h-3.5" />
                            )}
                            <span>{running ? "running…" : "Run"}</span>
                        </button>
                    </div>

                    <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-zinc-500 font-mono">
                        <Info className="w-3 h-3 mt-0.5 shrink-0" />
                        <span>
                            Runs as <span className="text-zinc-400">sudo -u www-data php /var/www/nextcloud/occ &lt;argv&gt;</span> on
                            the worker VM (via Proxmox guest-exec). Long verbs (app_api:*, app:install, occ upgrade …) run up to 30 min, otherwise 5 min · max 256KB output. Every invocation is audited.
                        </span>
                    </div>

                    {/* History */}
                    {history.length > 0 && (
                        <div className="mt-4 space-y-2" data-testid="occ-history">
                            <div className="flex items-center justify-between">
                                <div className="text-xs font-mono text-zinc-500 uppercase tracking-wider">
                                    History ({history.length})
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setHistory([])}
                                    className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
                                    data-testid="occ-clear-history"
                                >
                                    <Trash2 className="w-3 h-3" /> Clear
                                </button>
                            </div>
                            <div className="space-y-2 max-h-[460px] overflow-auto">
                                {history.map((h, idx) => (
                                    <OccHistoryItem key={idx} item={h} />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}


function OccHistoryItem({ item }) {
    const ok = item.exit_code === 0 && !item.error;
    const argvStr = item.argv?.join(" ") || "";
    const isErr = !!item.error;
    return (
        <div
            data-testid="occ-history-item"
            className={`border rounded-md ${
                isErr
                    ? "border-red-900/50 bg-red-950/20"
                    : ok
                        ? "border-zinc-800 bg-zinc-950/40"
                        : "border-amber-900/40 bg-amber-950/15"
            }`}
        >
            <div className="px-3 py-2 border-b border-zinc-900 flex items-center justify-between gap-2">
                <code className="font-mono text-xs text-zinc-300 truncate">
                    <span className="text-zinc-600">$ occ</span> {argvStr}
                </code>
                <div className="flex items-center gap-2 text-[11px] font-mono shrink-0">
                    <span
                        className={
                            isErr
                                ? "text-red-400"
                                : ok
                                    ? "text-emerald-400"
                                    : "text-amber-400"
                        }
                        data-testid="occ-exit-code"
                    >
                        rc={item.exit_code}
                    </span>
                    <span className="text-zinc-600">·</span>
                    <span className="text-zinc-500">{item.duration_ms}ms</span>
                </div>
            </div>

            {isErr && (
                <div className="px-3 py-2 text-xs text-red-300 flex items-start gap-2">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span className="font-mono">{item.error}</span>
                </div>
            )}

            {item.stdout && (
                <pre
                    data-testid="occ-stdout"
                    className="px-3 py-2 text-xs font-mono text-zinc-200 whitespace-pre-wrap break-all max-h-72 overflow-auto"
                >
                    {item.stdout}
                </pre>
            )}
            {item.stderr && (
                <pre
                    data-testid="occ-stderr"
                    className="px-3 py-2 text-xs font-mono text-amber-300/90 whitespace-pre-wrap break-all max-h-48 overflow-auto border-t border-zinc-900"
                >
                    {item.stderr}
                </pre>
            )}
            {item.truncated_output && (
                <div className="px-3 py-1 text-[11px] text-amber-400/80 font-mono border-t border-zinc-900">
                    ⚠ Output was truncated after 256KB.
                </div>
            )}
        </div>
    );
}
